import { useTheme } from '@emotion/react'
import {
    getShortDate,
    getTimeAgo,
    Profile,
    PublicationCardItem,
    scaleImage,
    Topic,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { filterName } from '../../bauhaus/helpers/filterName'
import {
    SNEntBylineText,
    StyledByline,
    StyledBylineName,
    StyledBylinePublicationDate,
    StyledBylineText,
    StyledBylineTextWithHeadshot,
    StyledHeadshot,
    StyledHeadshotWrapper,
    StyledSNEntAuthor,
    StyledSNEntBreadcrumb,
    StyledSNentTime,
} from '../../cards/CardByline/CardByline.styled'
import { CommonCardProps } from '../../cards/CardItem.Props'
import { Skeleton } from '../../content/Skeleton/Skeleton'
import { themedValue } from '../../__styling/themed-value'

export interface CardBylineProps extends CommonCardProps {
    className?: string
    hasBackground?: boolean
    showProfile?: boolean
    isEdgeToEdgeCard?: boolean
    item: MaybeLoaded<PublicationCardItem>
    isSNEntCard?: boolean
    showPublicationDate?: boolean
    displayPublicationTime?: boolean
}

export const CardByline: React.FC<CardBylineProps> = (props) => {
    const theme = useTheme()
    const {
        className,
        hasBackground,
        item,
        showProfile,
        isEdgeToEdgeCard,
        isSNEntCard,
        showPublicationDate,
    } = props

    const hasBackgroundColor = themedValue(theme, {
        perthnow: hasBackground,
        fallback: undefined,
    })

    if (!item.loaded) {
        return (
            <StyledByline className={className} hasPadding={false}>
                <Skeleton
                    count={1}
                    height={53}
                    dark={hasBackgroundColor || false}
                />
            </StyledByline>
        )
    }

    const publicationDate = item.result.publicationDate

    if (
        showProfile &&
        item.result.profiles &&
        item.result.profiles.length !== 0 &&
        !isSNEntCard
    ) {
        return renderBylineWithProfile(
            item.result.profiles,
            item.result.byline,
            !!isEdgeToEdgeCard,
            className,
        )
    }

    return (
        <StyledByline className={className} hasPadding={!!isEdgeToEdgeCard}>
            <StyledBylineText>
                {item.result.byline}
                {showPublicationDate && publicationDate && (
                    <StyledBylinePublicationDate>
                        {' | ' + getShortDate(publicationDate)}
                    </StyledBylinePublicationDate>
                )}
            </StyledBylineText>
        </StyledByline>
    )
}

const getBylineAuthorLink = (
    profiles: Profile[] | undefined,
    byline: string,
) => {
    if (profiles && profiles.length > 0) {
        return (
            <>
                {profiles.map((profile, i) => (
                    <>
                        {i > 0 && <> and </>}
                        <StyledSNEntAuthor
                            key={i}
                            to={`/profile/${profile.slug}`}
                        >
                            {profile.name}
                        </StyledSNEntAuthor>
                    </>
                ))}
            </>
        )
    }
    return getSNentAuthorName(byline)
}

const getTopicLink = (publicationKind: string, topic: Topic) => {
    let topicLink = `/${topic.id}`
    if (publicationKind === 'video') {
        topicLink = '/video' + topicLink
    }
    return topicLink
}

export const EntCardByline: React.FC<CardBylineProps> = (props) => {
    const { className, item } = props
    if (!item.loaded) {
        return (
            <StyledByline className={className} hasPadding={false}>
                <Skeleton count={1} height={53} />
            </StyledByline>
        )
    }

    const topicLink = getTopicLink(
        item.result.publicationKind,
        item.result.primaryTopic,
    )

    const byLine = item.result.byline
    return (
        <StyledByline className={className} hasPadding={false}>
            <SNEntBylineText>
                {getBylineAuthorLink(item.result.profiles, byLine)}
                <span> / </span>
                <StyledSNEntBreadcrumb to={topicLink}>
                    {filterName(item.result.primaryTopic.title)}
                </StyledSNEntBreadcrumb>
                {props.displayPublicationTime &&
                    item.result.publicationDate && (
                        <StyledSNentTime>
                            {' '}
                            / {getTimeAgo(item.result.publicationDate)}
                        </StyledSNentTime>
                    )}
            </SNEntBylineText>
        </StyledByline>
    )
}

function getSNentAuthorName(byline: string) {
    const formatted = byline.split(',')[0]
    return `By ${formatted}`
}

function getProfileImage(profile: Profile[]): string | undefined {
    const profileImage = profile.slice(0).map((item) => {
        return item.thumbnailPhoto ? item.thumbnailPhoto : undefined
    })[0]

    return profileImage ? scaleImage(profileImage, 110) : profileImage
}

function getProfileText(
    profile: Profile[] | undefined,
    text: string | undefined,
) {
    if (profile && profile.length !== 0) {
        const profileBlock = profile.map((item, index) => {
            return (
                <React.Fragment key={index}>
                    <StyledBylineName>{item.name}</StyledBylineName>
                </React.Fragment>
            )
        })

        return profileBlock
    }

    return text
}

function renderBylineWithProfile(
    profile: Profile[],
    byline: string,
    isEdgeToEdgeCard: boolean,
    className?: string,
) {
    const imageSrc = getProfileImage(profile)
    return (
        <StyledByline hasPadding={isEdgeToEdgeCard} className={className}>
            {imageSrc ? (
                <React.Fragment>
                    <StyledHeadshotWrapper>
                        <StyledHeadshot src={imageSrc} alt={byline} />
                    </StyledHeadshotWrapper>

                    <StyledBylineTextWithHeadshot>
                        {getProfileText(profile, byline)}
                    </StyledBylineTextWithHeadshot>
                </React.Fragment>
            ) : (
                <StyledBylineText>
                    {getProfileText(profile, byline)}
                </StyledBylineText>
            )}
        </StyledByline>
    )
}
CardByline.displayName = 'CardByline'
